import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { Store } from '@ngrx/store'
import { ScopeVersion } from '@app/core/model/scope-version'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { MenuOptions } from '@app/core/model/definitions/menu-options.interface'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'
import { FeeItem } from '@app/features/scope-overview/model/fee-item.model'
import { ScopeRetainedHoursStats } from '@app/features/scope-overview/model/scope-retained-hours-stats.model'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { CdnConfig } from '@app/core/model/cdn-config.model'
import { ScopingSelectors } from '@app/features/scoping/store/selectors/scoping.selector'
import { ScopeActions } from '@app/features/scoping/store/actions/scoping.actions'
import { untilDestroyed } from '@app/shared/utils/utils'
import { ChangeRequestActions } from '../../store/actions/change-request.actions'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { UpdateDeliverablePayload, UpdateRolePayload } from '@app/shared/components/scope-elements/models'
import { UpdateComponentPayload } from '@app/shared/components/scope-elements/models/update-component-payload.interface'
import { MatDialog } from '@angular/material/dialog'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { FetchDeliverablePayload } from '../../model'
import { AddEditFeeModalComponent } from '@app/features/scope-overview/components/scope-tab/add-edit-fee-modal/add-edit-fee-modal.component'
import { LanguageService } from '@app/core/service/language.service'
import { Department } from '@app/features/scoping/models/department.model'
import { EditDeliverablePayload } from '../../model/edit-deliverable-payload.interface'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ChangeRequestSelectors } from '../../store/selectors/change-request.selectors'

@Component({
  selector: 'change-request-overview',
  templateUrl: './change-request-overview.component.html',
})
export class ChangeRequestOverviewComponent implements OnChanges {
  @Input() currentScope: ScopeVersion
  @Input() currentChangeRequest: ChangeRequest
  @Input() isEditable: boolean
  @Input() cdnConfig: CdnConfig

  @Input() mainColumnClass: string
  @Input() componentMainColumnClass: string
  @Input() roleMainColumnClass: string

  @Output() onFetchDeliverable = new EventEmitter<FetchDeliverablePayload>()
  @Output() onEditDeliverable = new EventEmitter<EditDeliverablePayload>()
  @Output() onAddDeliverable = new EventEmitter<Deliverable>()
  @Output() onDeleteDeliverable = new EventEmitter<Deliverable>()
  @Output() onDeleteComponent = new EventEmitter<ScopeComponent>()

  scopeRetainedHoursStats$ = this.store$.select(ScopeOverviewSelectors.selectScopeRetainedHourStats)
  rateCard$ = this.store$.select(ScopingSelectors.selectRateCard)

  deliverables$ = this.store$.select(ChangeRequestSelectors.selectMappedUnsectionedDeliverables)
  sections$ = this.store$.select(ChangeRequestSelectors.selectMappedDeliverableSections(false))

  feeMenuOptions: MenuOptions[] = []
  deliverableMenuOptions: MenuOptions[] = []
  componentMenuOptions: MenuOptions[] = []
  sectionMenuOptions: MenuOptions[] = []

  currentRatecard: RatecardVersion
  scopeRetainedHoursStats: ScopeRetainedHoursStats
  showUseRetainedTeamToggle = false
  selectedScopeId: number

  constructor(
    private store$: Store,
    public scopeElementsService: ScopeElementsService,
    private dialog: MatDialog,
    private lang: LanguageService
  ) {
    this.destroy$ = untilDestroyed()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentScope && this.currentScope && this.selectedScopeId != this.currentScope?.identity?.id) {
      this.initializeTableData()
    }

    this.selectedScopeId = this.currentScope?.identity?.id

    if (changes.isEditable) this.setMenu()
  }

  fetchDeliverable(deliverableId: number) {
    this.onFetchDeliverable.emit({ scopeId: this.selectedScopeId, deliverableId })
  }

  deleteSection(section: ScopeSection): void {
    this.store$.dispatch(ChangeRequestActions.deleteSection({ sectionId: section.id }))
  }

  updateDeliverable({ deliverable, changes }: UpdateDeliverablePayload): void {
    this.store$.dispatch(
      ChangeRequestActions.modifyDeliverable({
        identity: deliverable.changeAuditData?.identity || deliverable.id,
        changes,
      })
    )
  }

  updateComponent({ component, changes }: UpdateComponentPayload): void {
    //console.info({ component, changes });
    this.store$.dispatch(
      ChangeRequestActions.modifyComponent({
        identity: component.changeAuditData?.identity || component.id,
        parentId: component.deliverable?.changeAuditData?.identity || component.deliverable?.id,
        changes,
      })
    )
  }

  addScopeFee(): void {
    this.addFeeItem(this.lang.get('scope|l'), (data: FeeItem) =>
      this.store$.dispatch(ChangeRequestActions.addScopeFee({ data }))
    )
  }

  addDeliverableFee(deliverable: Deliverable): void {
    this.addFeeItem(this.lang.get('deliverable|l'), (data: FeeItem) =>
      this.store$.dispatch(
        ChangeRequestActions.addDeliverableFee({
          identity: data.id,
          parentId: deliverable.changeAuditData?.identity || deliverable.id,
          data,
        })
      )
    )
  }

  addFeeItem(title: string, callback: (data: FeeItem) => void): void {
    const dialog = this.dialog.open(AddEditFeeModalComponent)
    this.store$.dispatch(ScopeActions.getAllFeeItems())
    dialog.componentInstance.existingFees$ = this.store$.select(ScopingSelectors.selectAllFeeItems)
    dialog.componentInstance.currencyCode = this.currentScope.identity?.rateCard.currencyCode
    dialog.componentInstance.modalTitle = `Add ${title} fee`

    dialog.componentInstance.onSubmit.subscribe((event: { feeItem: FeeItem }) => {
      dialog.close()
      callback(event.feeItem)
    })
  }

  updateFeeItem(payload: any): void {
    this.store$.dispatch(
      ChangeRequestActions.updateFeeItem({
        feeItemId: payload.id || payload.identity,
        data: payload,
      })
    )
  }

  updateRole({ component, role }: UpdateRolePayload): void {
    const departments = component.departments
      .map((department) => ({
        ...department,
        roles: department.roles.map((r) => (r.id === role.id ? role : r)).filter((r) => !r.isRemoved),
      }))
      .filter((d) => d.roles.length)

    this.updateComponent({
      component,
      changes: { departments: { old: component.departments, new: departments as Department[] } },
    })
  }

  private initializeTableData() {
    this.setMenu()

    this.scopeRetainedHoursStats$
      .pipe(this.destroy$())
      .subscribe((stats: ScopeRetainedHoursStats) => (this.scopeRetainedHoursStats = stats))
    this.rateCard$.pipe(this.destroy$()).subscribe((ratecard: RatecardVersion) => (this.currentRatecard = ratecard))
  }

  private setMenu(): void {
    this.deliverableMenuOptions = []
    this.sectionMenuOptions = []
    this.componentMenuOptions = []

    if (this.isEditable) {
      this.deliverableMenuOptions = [
        {
          isDetached: () => true,
          callback: (deliverable: Deliverable) =>
            this.onEditDeliverable.emit({
              scopeId: this.selectedScopeId,
              changeRequestId: this.currentChangeRequest.id,
              deliverableId: deliverable.id,
            }),
          name: () => 'Edit {LANG:deliverable}',
          icon: () => 'mode_edit',
        },
        {
          callback: (deliverable: Deliverable) => this.onDeleteDeliverable.emit(deliverable),
          name: () => `Delete`,
          icon: () => 'delete',
        },
        {
          hasPrivilege: () => Privilege.SCOPE__CREATE_FEES,
          callback: (deliverable: Deliverable) => this.addDeliverableFee(deliverable),
          name: () => `Add fee`,
          icon: () => 'attach_money',
        },
      ]

      this.sectionMenuOptions = [
        { callback: (element) => this.deleteSection(element.entity), icon: () => 'delete', name: () => `Delete` },
      ]

      this.componentMenuOptions = [
        {
          callback: (element) => this.onDeleteComponent.emit(element),
          name: () => 'Delete',
          icon: () => 'delete',
        },
      ]
    }
  }

  protected readonly destroy$
}
